'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../utils/cn';
import { Alert, type alertVariants } from './Alert';
import { Button } from './Button';
function readDismissed(key: string): boolean {
  if (typeof localStorage === 'undefined' || typeof localStorage.getItem !== 'function') return false;
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}
function writeDismissed(key: string): void {
  if (typeof localStorage === 'undefined' || typeof localStorage.setItem !== 'function') return;
  try {
    localStorage.setItem(key, 'true');
  } catch {
    // ignore quota / security errors
  }
}
interface DismissibleAlertProps extends Omit<React.ComponentProps<'div'>, 'key'>, VariantProps<typeof alertVariants> {
  /**
   * When provided, dismissal state is persisted in `localStorage` under this key so the alert
   * stays dismissed across reloads. When omitted, dismissal is session-only (component state).
   */
  storageKey?: string;
  /**
   * Icon rendered to the left of the alert content. See `Alert` for default behavior — the
   * variant-specific icon is used when this prop is omitted.
   */
  icon?: React.ReactNode | null | false;
  /**
   * When false the alert is always visible and no dismiss button is shown.
   * Defaults to true.
   */
  dismissible?: boolean;
}
function DismissibleAlert(t0) {
  const $ = _c(27);
  let children;
  let className;
  let icon;
  let props;
  let storageKey;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      storageKey,
      variant,
      className,
      icon,
      dismissible: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = props;
    $[5] = storageKey;
    $[6] = t1;
    $[7] = variant;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    props = $[4];
    storageKey = $[5];
    t1 = $[6];
    variant = $[7];
  }
  const dismissible = t1 === undefined ? true : t1;
  const [dismissed, setDismissed] = React.useState(null);
  let t2;
  let t3;
  if ($[8] !== dismissible || $[9] !== storageKey) {
    t2 = () => {
      if (!dismissible) {
        setDismissed(false);
        return;
      }
      setDismissed(storageKey ? readDismissed(storageKey) : false);
    };
    t3 = [storageKey, dismissible];
    $[8] = dismissible;
    $[9] = storageKey;
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  React.useEffect(t2, t3);
  if (dismissed !== false) {
    return null;
  }
  let t4;
  if ($[12] !== storageKey) {
    t4 = function handleDismiss() {
      if (storageKey) {
        writeDismissed(storageKey);
      }
      setDismissed(true);
    };
    $[12] = storageKey;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleDismiss = t4;
  const t5 = dismissible && "pr-10";
  let t6;
  if ($[14] !== className || $[15] !== t5) {
    t6 = cn("relative", t5, className);
    $[14] = className;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== dismissible || $[18] !== handleDismiss) {
    t7 = dismissible && <Button variant="ghost" size="iconSm" onClick={handleDismiss} aria-label="Dismiss" className="absolute top-2 right-2 opacity-70 hover:opacity-100"><X /></Button>;
    $[17] = dismissible;
    $[18] = handleDismiss;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== children || $[21] !== icon || $[22] !== props || $[23] !== t6 || $[24] !== t7 || $[25] !== variant) {
    t8 = <Alert variant={variant} icon={icon} className={t6} {...props}>{children}{t7}</Alert>;
    $[20] = children;
    $[21] = icon;
    $[22] = props;
    $[23] = t6;
    $[24] = t7;
    $[25] = variant;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
export { DismissibleAlert };